<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="rs-question">
        <span class="rs-bar rs-bar--xl"></span>
      </div>
      <div class="rs-summary">
        <div class="rs-avg">
          <span class="rs-avg-num">4.2</span>
          <span class="rs-avg-out">/ 5</span>
        </div>
        <div class="rs-stars">
          <span
            v-for="n in 5"
            :key="n"
            class="material-symbols-rounded rs-star"
            :class="{ '--filled': n <= 4, '--half': n === 5 }"
          >star</span>
        </div>
      </div>
      <div class="rs-dist">
        <div
          v-for="(b, i) in dist"
          :key="b.label"
          class="rs-dist-row"
          :style="{ '--row-i': i }"
        >
          <span class="rs-dist-label">{{ b.label }}</span>
          <div class="rs-dist-track">
            <div
              class="rs-dist-fill"
              :style="{ '--row-pct': b.pct + '%', '--row-i': i }"
            ></div>
          </div>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="rs-phone-q"></span>
      <div class="rs-phone-stars">
        <span
          v-for="n in 5"
          :key="n"
          class="material-symbols-rounded rs-phone-star"
          :class="{ '--filled': n <= 4, '--tap': n === 4 }"
          :style="{ '--star-i': n }"
        >star</span>
      </div>
      <div class="rs-phone-hint">
        Tap a star to rate
      </div>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewRatingScale',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      dist: [
        { label: '5', pct: 56 },
        { label: '4', pct: 28 },
        { label: '3', pct: 10 },
        { label: '2', pct: 4 },
        { label: '1', pct: 2 },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.rs-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
  width: 70%;
}
.rs-question {
  padding-bottom: 6px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.rs-summary {
  display: flex; align-items: center; gap: 12px;
  padding: 6px 0;
}
.rs-avg { display: flex; align-items: baseline; gap: 2px; }
.rs-avg-num {
  font-size: 28px; font-weight: 800;
  color: var(--accent);
  font-variant-numeric: tabular-nums;
}
.rs-avg-out {
  font-size: 12px; font-weight: 700;
  color: rgba(15, 15, 20, 0.4);
}
.rs-stars { display: flex; gap: 2px; }
.rs-star {
  font-size: 18px;
  color: rgba(15, 15, 20, 0.18);
}
.rs-star.--filled { color: var(--accent); }
.rs-star.--half {
  color: var(--accent);
  font-variation-settings: 'FILL' 0;
}

.rs-dist { display: grid; gap: 5px; }
.rs-dist-row {
  display: grid;
  grid-template-columns: 12px 1fr;
  align-items: center;
  gap: 6px;
}
.rs-dist-label {
  font-size: 9px; font-weight: 700;
  color: rgba(15, 15, 20, 0.5);
  text-align: right;
}
.rs-dist-track {
  height: 6px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  overflow: hidden;
}
.rs-dist-fill {
  height: 100%; width: 0;
  border-radius: 999px;
  background: var(--accent);
  animation: rs-grow 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--row-i, 0) * 80ms + 700ms);
}

/* ---- Audience phone ---- */
.rs-phone-q {
  display: block; height: 8px; border-radius: 999px;
  width: 70%; background: rgba(15, 15, 20, 0.18);
  margin-bottom: 6px;
}
.rs-phone-stars {
  display: flex; justify-content: center; gap: 4px;
  padding: 16px 0 12px;
}
.rs-phone-star {
  font-size: 26px;
  color: rgba(15, 15, 20, 0.18);
}
.rs-phone-star.--filled {
  color: var(--accent);
  animation: rs-fill 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--star-i, 1) * 90ms + 600ms);
}
.rs-phone-star.--tap {
  animation: rs-pop 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
}
.rs-phone-hint {
  text-align: center;
  font-size: 10px; font-weight: 700;
  color: var(--accent);
}

@keyframes rs-grow {
  0%, 12% { width: 0; }
  60%     { width: var(--row-pct, 50%); }
  92%, 100% { width: var(--row-pct, 50%); }
}
@keyframes rs-fill {
  0%, 12% { color: rgba(15, 15, 20, 0.18); transform: scale(1); }
  35%     { color: var(--accent); transform: scale(1.2); }
  50%, 100% { color: var(--accent); transform: scale(1); }
}
@keyframes rs-pop {
  0%, 25%   { transform: scale(1); }
  35%, 45%  { transform: scale(1.4); }
  60%, 100% { transform: scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .rs-dist-fill { animation: none; width: var(--row-pct, 50%); }
  .rs-phone-star.--filled, .rs-phone-star.--tap { animation: none; color: var(--accent); }
}
</style>
